/* Journal Home — Aperture / Monocle / printed-monograph contents page.

   Masthead → CONTENTS sequence of numbered chapters → BOOK AN ENTRY CTA.
   Serif-heavy. Roman numerals. Gold hairlines between rows. */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

const PLACEHOLDER_ENTRIES = [
  { title: "Spring Editorial", date: "MMXXVI · April", excerpt: "A portrait sequence captured on the verge of bloom — silk, breath, and the held-back warmth of an early morning." },
  { title: "Brand Story · Maison V.", date: "MMXXVI · March", excerpt: "Three days inside the atelier of a quiet master, photographing hands more than faces, threads more than people." },
  { title: "The Wedding of N. & E.", date: "MMXXVI · February", excerpt: "Two ceremonies, one continent crossed. Vows in two languages, photographed in one." },
  { title: "Studio Notebook · 003", date: "MMXXV · December", excerpt: "Lighting tests, frame fragments, and pages from the wall — what we keep between assignments." },
  { title: "Autumn Sittings", date: "MMXXV · November", excerpt: "A season of dignified portraits — fathers and daughters, mothers and grown sons, captured slow." },
];

export default function JournalHome() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const tagline = settings?.tagline || settings?.hero_descriptor || "A quarterly of considered photographs.";
  const portfolio = settings?.portfolio_images || [];
  const season = getSeason();
  const year = new Date().getFullYear();

  // Use the studio's actual portfolio (if any) as thumbnails for the first N chapters
  const entries = PLACEHOLDER_ENTRIES.map((e, i) => ({ ...e, thumb: portfolio[i] || null }));

  useEffect(() => {
    document.title = `${studioName} — A Quarterly`;
    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", `${studioName}. ${tagline}`);
  }, [studioName, tagline]);

  return (
    <div
      className="min-h-screen"
      style={{
        background: "var(--tenant-bg, #F5F2EC)",
        color: "var(--tenant-fg, #0E0D0C)",
        fontFamily: "var(--tenant-serif)",
      }}
      data-testid="journal-home"
    >
      {/* Paper texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle at 25% 35%, #000 0.5px, transparent 0.7px), radial-gradient(circle at 80% 65%, #000 0.5px, transparent 0.7px)",
          backgroundSize: "4px 4px, 7px 7px",
        }}
        aria-hidden
      />

      {/* Masthead band */}
      <section className="pt-20 pb-12 px-6 lg:px-12 max-w-5xl mx-auto" data-testid="journal-masthead">
        <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-3" data-testid="journal-quarterly-line">
          A quarterly of {studioName.toUpperCase()} · {season.toUpperCase()} {year}
        </p>
        <h1
          className="font-light italic leading-[1.0] tracking-[-0.01em]"
          style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3rem, 8vw, 6rem)" }}
        >
          {studioName}
        </h1>
        <p className="mt-5 text-lg sm:text-xl italic text-[color:var(--tenant-fg,#0E0D0C)]/70 max-w-2xl">
          {tagline}
        </p>
      </section>

      {/* Gold hairline */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div className="h-px" style={{ background: "var(--tenant-accent, #C9A961)" }} />
      </div>

      {/* CONTENTS */}
      <section className="py-12 lg:py-20 max-w-5xl mx-auto px-6 lg:px-12" data-testid="journal-contents">
        <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-10">
          Contents · {season} {year}
        </div>

        <ul className="divide-y border-y" style={{ borderColor: "var(--tenant-accent-line, rgba(201,169,97,0.35))", "--tw-divide-opacity": 1 }}>
          {entries.map((entry, i) => (
            <li key={i} className="grid grid-cols-12 gap-5 py-7 items-center" data-testid={`journal-chapter-${i}`}
                style={{ borderTop: i === 0 ? "none" : "1px solid var(--tenant-accent-line, rgba(201,169,97,0.35))" }}>
              {/* Thumbnail */}
              <div className="col-span-2 sm:col-span-1">
                <div className="w-14 h-14 sm:w-20 sm:h-20 overflow-hidden" style={{ background: "rgba(var(--tenant-fg-rgb, 14,13,12), 0.10)" }}>
                  {entry.thumb ? (
                    <img src={optimizeImageUrl(entry.thumb, { w: 320 })}
                      srcSet={optimizedSrcSet(entry.thumb, [160, 240, 320, 480]) || undefined}
                      sizes="80px"
                      alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-widest text-[color:var(--tenant-fg,#0E0D0C)]/40">
                      {ROMAN[i]}
                    </div>
                  )}
                </div>
              </div>
              {/* Title & date */}
              <div className="col-span-10 sm:col-span-7">
                <div className="text-2xl sm:text-3xl font-light italic leading-tight" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <span className="text-[color:var(--tenant-accent)] mr-2">Chapter {ROMAN[i]}</span>
                  <span className="text-[color:var(--tenant-fg,#0E0D0C)]/30 mx-1">—</span>
                  {entry.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent)] mt-2">
                  {entry.date}
                </div>
              </div>
              {/* Excerpt */}
              <div className="col-span-12 sm:col-span-4 text-sm leading-relaxed text-[color:var(--tenant-fg,#0E0D0C)]/70 italic" style={{ fontFamily: "var(--tenant-serif)" }}>
                {entry.excerpt}
              </div>
            </li>
          ))}
        </ul>

        {/* Closing CTAs — serif italic gold-underlined links, NOT buttons */}
        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-baseline gap-7">
          <Link
            to={tenantHref("/booking")}
            className="text-2xl italic transition-opacity hover:opacity-70"
            style={{ fontFamily: "var(--tenant-serif)", borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}
            data-testid="journal-book-entry"
          >
            Book an entry →
          </Link>
          <Link
            to={tenantHref("/portfolio")}
            className="text-lg italic text-[color:var(--tenant-fg,#0E0D0C)]/70 hover:text-[color:var(--tenant-fg,#0E0D0C)] transition-colors"
            style={{ fontFamily: "var(--tenant-serif)" }}
            data-testid="journal-read-issue"
          >
            Read the full issue →
          </Link>
        </div>
      </section>
    </div>
  );
}
